import { getStateOptions } from '@/helpers/getStateOptions';
import { Box, Button, Modal, SelectInputNative, TextInput } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ProgramType, StateLicenseNumber, StateLicenseStatus } from 'types';

import { mapLicenseSaveErrors } from './licenseErrors';

// Everything is held as a string while editing; submit converts and drops blanks.
interface FormValues {
  status: StateLicenseStatus;
  state: string;
  contractorLicenseNumber: string;
  monetaryLimit: string;
  additionalLicenseNumbers: string;
  legalEntityName: string;
  legalEntityFein: string;
}

export interface LicenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  programType: ProgramType;
  /** Absent means add mode. */
  license?: StateLicenseNumber;
  /** States already licensed for this program type, excluding the license being edited. */
  takenStates: string[];
  onSubmit: (license: StateLicenseNumber) => Promise<void>;
}

const STATUS_OPTIONS = [
  { label: 'Active', value: StateLicenseStatus.active },
  { label: 'Inactive', value: StateLicenseStatus.inactive },
  { label: 'Suspended', value: StateLicenseStatus.suspended },
];

// The modal shrinks to its content, so the grid sets an explicit width instead of relying on 100%.
const GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '16px',
  width: 'min(640px, calc(100vw - 96px))',
};

const LEGAL_ENTITY_HELP_TEXT = "Set both or leave both blank. Blank fields print the organization's name and FEIN.";
const REQUIRED_MESSAGE = 'Required';
const REQUIRED_RULES = { required: REQUIRED_MESSAGE } as const;
const NON_BLANK_RULES = {
  validate: (v: string) => v.trim() !== '' || REQUIRED_MESSAGE,
} as const;
// Exported because browsers blank out overflowing number input, so the Infinity case can't be driven through the DOM.
export const validateMonetaryLimit = (v: string) =>
  v.trim() === '' || (Number.isFinite(Number(v)) && Number(v) >= 0) || 'Must be >= 0';
const MONETARY_LIMIT_RULES = { validate: validateMonetaryLimit } as const;

const toFormValues = (license?: StateLicenseNumber): FormValues => ({
  status: license?.status ?? StateLicenseStatus.active,
  state: license?.state ?? '',
  contractorLicenseNumber: license?.contractorLicenseNumber ?? '',
  monetaryLimit: license?.monetaryLimit == null ? '' : String(license.monetaryLimit),
  additionalLicenseNumbers: license?.additionalLicenseNumbers ?? '',
  legalEntityName: license?.legalEntityName ?? '',
  legalEntityFein: license?.legalEntityFein ?? '',
});

export function LicenseModal({ isOpen, onClose, programType, license, takenStates, onSubmit }: LicenseModalProps) {
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    getValues,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({ defaultValues: toFormValues(license) });

  useEffect(() => {
    if (isOpen) {
      reset(toFormValues(license));
      setGeneralErrors([]);
    }
  }, [isOpen, license, reset]);

  const stateOptions = useMemo(
    () => [
      { label: 'Select a state...', value: '' },
      ...getStateOptions(true).filter((option) => !takenStates.includes(option.value)),
    ],
    [takenStates],
  );

  // The key is the field that is missing; the error belongs on that field, not the one that was filled.
  const legalEntityNameRules = useMemo(
    () => ({
      validate: (value: string) =>
        value.trim() !== '' ||
        getValues('legalEntityFein').trim() === '' ||
        'Legal entity name is required when a legal entity FEIN is set.',
    }),
    [getValues],
  );
  const legalEntityFeinRules = useMemo(
    () => ({
      validate: (value: string) =>
        value.trim() !== '' ||
        getValues('legalEntityName').trim() === '' ||
        'Legal entity FEIN is required when a legal entity name is set.',
    }),
    [getValues],
  );

  const submit = useCallback(
    async (data: FormValues) => {
      setGeneralErrors([]);

      const additionalLicenseNumbers = data.additionalLicenseNumbers.trim();
      const monetaryLimit = data.monetaryLimit.trim();
      const legalEntityName = data.legalEntityName.trim();
      const legalEntityFein = data.legalEntityFein.trim();

      // Start from the existing license so fields this form doesn't edit survive, then drop
      // optional keys that are blank so a cleared value is removed rather than left stale.
      const {
        monetaryLimit: _monetaryLimit,
        additionalLicenseNumbers: _additional,
        legalEntityName: _name,
        legalEntityFein: _fein,
        ...preserved
      } = license ?? ({} as Partial<StateLicenseNumber>);

      const result: StateLicenseNumber = {
        ...preserved,
        state: data.state,
        contractorLicenseNumber: data.contractorLicenseNumber.trim(),
        programType,
        status: data.status,
        ...(monetaryLimit !== '' && { monetaryLimit: Number(monetaryLimit) }),
        ...(additionalLicenseNumbers !== '' && { additionalLicenseNumbers }),
        ...(legalEntityName !== '' && { legalEntityName }),
        ...(legalEntityFein !== '' && { legalEntityFein }),
      };

      try {
        await onSubmit(result);
      } catch (error) {
        const { fieldErrors, general } = mapLicenseSaveErrors(error);
        (Object.keys(fieldErrors) as Array<keyof typeof fieldErrors>).forEach((fieldName) => {
          setError(fieldName, {
            type: 'server',
            message: fieldErrors[fieldName],
          });
        });
        setGeneralErrors(general);
      }
    },
    [license, onSubmit, programType, setError],
  );

  // Closing mid-save would unmount the modal and lose the result or error of the in-flight request.
  const handleDismiss = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [isSubmitting, onClose]);

  const handleFormSubmit = useCallback(() => void handleSubmit(submit)(), [handleSubmit, submit]);

  return (
    <Modal isOpen={isOpen} onDismiss={handleDismiss} maxWidth="720px" ariaLabelledBy="license-modal">
      <Modal.Header id="license-modal" title={license ? 'Edit License' : 'Add License'} onDismiss={handleDismiss} />
      <Modal.Body background="secondary" childGap="lg" padding="lg">
        {generalErrors.length > 0 && (
          <Box
            role="alert"
            background="danger-100"
            borderWidth="xs"
            borderColor="danger"
            padding="sm md"
            fontSize="sm"
          >
            {generalErrors.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </Box>
        )}

        <Box childGap="md">
          <Box fontWeight="bold">License</Box>
          <div style={GRID_STYLE}>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <SelectInputNative
                  id="license-status"
                  label="Status"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  options={STATUS_OPTIONS}
                />
              )}
            />
            <Controller
              control={control}
              name="state"
              rules={REQUIRED_RULES}
              render={({ field }) => (
                <SelectInputNative
                  id="license-state"
                  label="State"
                  isRequired
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  options={stateOptions}
                  error={errors.state?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="contractorLicenseNumber"
              rules={NON_BLANK_RULES}
              render={({ field }) => (
                <TextInput
                  {...field}
                  id="license-number"
                  label="License Number"
                  isRequired
                  error={errors.contractorLicenseNumber?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="monetaryLimit"
              rules={MONETARY_LIMIT_RULES}
              render={({ field }) => (
                <TextInput
                  {...field}
                  type="number"
                  id="license-monetary-limit"
                  label="Monetary Limit"
                  error={errors.monetaryLimit?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="additionalLicenseNumbers"
              render={({ field }) => (
                <TextInput {...field} id="license-additional-numbers" label="Additional License Numbers" />
              )}
            />
          </div>
        </Box>

        <Box childGap="md">
          <Box childGap="xs">
            <Box fontWeight="bold">Legal Entity (optional)</Box>
            <Box fontSize="sm" color="grey-500">
              {LEGAL_ENTITY_HELP_TEXT}
            </Box>
          </Box>
          <div style={GRID_STYLE}>
            <Controller
              control={control}
              name="legalEntityName"
              rules={legalEntityNameRules}
              render={({ field }) => (
                <TextInput
                  {...field}
                  id="license-legal-entity-name"
                  label="Legal Entity Name"
                  error={errors.legalEntityName?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="legalEntityFein"
              rules={legalEntityFeinRules}
              render={({ field }) => (
                <TextInput
                  {...field}
                  id="license-legal-entity-fein"
                  label="Legal Entity FEIN"
                  error={errors.legalEntityFein?.message}
                />
              )}
            />
          </div>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" tone="neutral" isDisabled={isSubmitting} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" isLoading={isSubmitting} isDisabled={isSubmitting} onClick={handleFormSubmit}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
